import React from 'react';
import { View, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import Typography from 'src/components/Text/Text';
import { themes } from 'src/theme/theme';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/store';
import { setActiveTab } from 'src/slices/UiSlice';
import { useLinkTo } from '@react-navigation/native';
import { DRIVER_BYPASS_URL, VEHICLE_BYPASS_URL } from 'src/routes/urlConstants';
import SidebarAppInfo from './SidebarAppInfo';
import SidebarNotesWidget from './SidebarNotesWidget';

type SideNavigationTab = {
  label: string;
  route: string;
  disabled: boolean;
};

interface SideNavigationProps {
  tabs: SideNavigationTab[];
  width: number;
  showProducerExtras?: boolean;
}

const SideNavigation: React.FC<SideNavigationProps> = ({
  tabs,
  width,
  showProducerExtras = false,
}) => {
  const activeTab = useSelector((state: RootState) => state.UI.activeTab);
  const drivers = useSelector((state: RootState) => state.drivers.drivers);
  const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
  const filledTabsIndex = useSelector((state: RootState) => state.UI.filledTabsIndex);

  const dispatch = useDispatch();
  const linkTo = useLinkTo();

  const isTabDisabled = (index: number): boolean => {
    if (index >= 1 && index <= 3) return false;
    return index > filledTabsIndex;
  };

  const handleTabPress = (index: number, route: string) => {
    if (index === 2 && drivers.length > 0) {
      linkTo(DRIVER_BYPASS_URL);
      dispatch(setActiveTab({ activeTab: index }));
      return;
    }
    if (index === 1 && vehicles.length > 0) {
      linkTo(VEHICLE_BYPASS_URL);
      dispatch(setActiveTab({ activeTab: index }));
      return;
    }
    dispatch(setActiveTab({ activeTab: index }));
    linkTo(route);
  };

  return (
    <View style={[styles.container, { width }]}>
      <View style={styles.header}>
        <Typography variant="h3" style={styles.headerText}>
          Steps
        </Typography>
      </View>
      <ScrollView showsVerticalScrollIndicator={false}>
        {tabs.map((tab, index) => {
          const isActive = activeTab === index;
          const disabled = isTabDisabled(index);
          const isCompleted = index < filledTabsIndex;

          return (
            <TouchableOpacity
              key={index}
              testID={`side-nav-tab-${index}`}
              style={[styles.tab, isActive && styles.tabActive, disabled && styles.tabDisabled]}
              onPress={() => !disabled && handleTabPress(index, tab.route)}
              disabled={disabled}>
              <View
                style={[
                  styles.stepIndicator,
                  isActive && styles.stepIndicatorActive,
                  isCompleted && styles.stepIndicatorCompleted,
                ]}>
                <Typography
                  variant="caption1Medium"
                  style={
                    isActive || isCompleted
                      ? [styles.stepNumber, styles.stepNumberActive]
                      : styles.stepNumber
                  }>
                  {isCompleted ? '\u2713' : `${index + 1}`}
                </Typography>
              </View>
              <View style={styles.tabContent}>
                <Typography
                  variant={isActive ? 'title3' : 'body2'}
                  style={disabled ? [styles.tabLabel, styles.tabLabelDisabled] : styles.tabLabel}>
                  {tab.label.trim()}
                </Typography>
                {index === 1 && vehicles.length > 0 && (
                  <Typography variant="caption2" style={styles.countBadge}>
                    {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} added
                  </Typography>
                )}
                {index === 2 && drivers.length > 0 && (
                  <Typography variant="caption2" style={styles.countBadge}>
                    {drivers.length} driver{drivers.length !== 1 ? 's' : ''} added
                  </Typography>
                )}
              </View>
            </TouchableOpacity>
          );
        })}

        {showProducerExtras && (
          <>
            <SidebarAppInfo />
            <SidebarNotesWidget />
          </>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: themes.light.white[1],
    borderRightWidth: 1,
    borderRightColor: themes.light.gray[7],
    paddingTop: 16,
  },
  header: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: themes.light.gray[7],
    marginBottom: 8,
  },
  headerText: {
    color: themes.light.gray_blue[1],
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    gap: 12,
  },
  tabActive: {
    backgroundColor: themes.light.brand_blue[7],
    borderRightWidth: 3,
    borderRightColor: themes.light.brand_blue[3],
  },
  tabDisabled: {
    opacity: 0.4,
  },
  stepIndicator: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: themes.light.gray[7],
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepIndicatorActive: {
    backgroundColor: themes.light.brand_blue[3],
  },
  stepIndicatorCompleted: {
    backgroundColor: themes.light.green[3],
  },
  stepNumber: {
    color: themes.light.gray[5],
  },
  stepNumberActive: {
    color: themes.light.white[1],
  },
  tabContent: {
    flex: 1,
  },
  tabLabel: {
    color: themes.light.gray_blue[1],
  },
  tabLabelDisabled: {
    color: themes.light.gray[6],
  },
  countBadge: {
    color: themes.light.green[2],
    fontSize: 11,
    marginTop: 2,
  },
});

export default SideNavigation;
